import { defineStore } from 'pinia'
import { apiService } from '@/services/api'
import { formatDate } from '@/components/logics/dateUtil'
import { useRegionStore } from '@/stores/regionStore'
import { useUserStore } from '@/stores/userStore'

export const use36AgreementStore = defineStore('article36', {
  state: () => ({
    // 36協定リスト
    agreements: [],

    // テーブル表示用リスト定義
    tableList: [],

    // 編集用フォームデータ
    formData: null,

    // フォームの初期値
    formInit: {
      id: null,
      contractId: null,
      status: 'in_progress',
      companyId: null,
      branchId: null,
      specialClause: '',
      scheduledEndDate: null,
      laborBureauCode: '',
      laborOfficeCode: '',
      memo: '',
      dateJson: {
        endDate: {
          isChecked: false,
          date: null,
          userId: null,
          userName: '',
        },
      },
      colorInfo: {
        status: '',
        companyId: '',
        branchId: '',
        specialClause: '',
        scheduledEndDate: '',
        laborBureauCode: '',
        laborOfficeCode: '',
        endDate: '',
        memo: '',
      },
      egovId: null,
    },

    // 軽量な選択肢データ
    companyNames: [],
    branchNames: [],

    // 設定情報
    config: {},

    // ステータスマッピング
    STATUS_MAP: {
      in_progress: '申請中',
      waiting: '待ち',
      completed: '完了',
      confirmation: '確認',
    },

    // 削除された事業場かどうかのフラグ
    isDeletedBranch: false,
  }),

  actions: {
    // テーブル表示用のリストを初期化
    initTableList() {
      this.tableList = [
        {
          label: 'No',
          key: 'rowNumber',
          data: (item, value) => value,
        },
        {
          label: '会社名',
          key: 'companyId',
          data: (item, value) => item.companyName || '削除された会社',
        },
        {
          label: '事業場名',
          key: 'branchId',
          data: (item, value) => item.branchName || '削除された事業場',
        },
        {
          label: '状況',
          key: 'status',
          data: (item, value) => this.getStatusLabel(item.status),
          tag: {
            申請中: 'application-in-progress',
            待ち: 'pending',
            完了: 'completed',
            確認: 'awaiting-confirmation',
          },
          tag_css: 'wide',
        },
        {
          label: 'e-Gov状況',
          key: 'egovStatus',
          data: (item, value) => item.egovStatus || 'e-Gov電子申請',
          action: (table, item, event) => {
            event.stopPropagation()
          },
          actionEnabled: (table, item) => !item.egovStatus, // egovStatusがnullの時のみアクション有効
          css: item => (item.egovStatus ? '' : 'btn'), // egovStatusがnullの時のみbtnクラス
        },
        {
          label: '特別条項',
          key: 'specialClause',
          data: item => item.specialClause || '-',
        },
        {
          label: '終了予定日',
          key: 'scheduledEndDate',
          data: item => (item.scheduledEndDate ? formatDate(item.scheduledEndDate, '-') : '-'),
        },
        {
          label: '終了日',
          key: 'endDate',
          data: item => this.getDateFromJson(item, 'endDate'),
        },
        {
          label: '管轄',
          key: 'laborBureauCode',
          data: item => this.getLaborBureauName(item.laborBureauCode),
        },
        {
          label: '監督署',
          key: 'laborOfficeCode',
          data: item => this.getLaborOfficeName(item.laborOfficeCode),
        },
        {
          label: '備考',
          key: 'memo',
          data: item => item.memo || '-',
        },
      ]
    },

    // 労働局名を取得するヘルパーメソッド
    getLaborBureauName(code) {
      if (!code) return '-'

      const regionStore = useRegionStore()
      const prefectureName = regionStore.prefectureCodesMap[code]

      return prefectureName ? `${prefectureName}労働局` : '-'
    },

    // 監督署名を取得するヘルパーメソッド
    getLaborOfficeName(code) {
      if (!code) return '-'

      const regionStore = useRegionStore()
      const officeName = regionStore.getOfficeNameByCode(code)

      return officeName ?? '-'
    },

    // ステータス値から表示ラベルを取得
    getStatusLabel(status) {
      return this.STATUS_MAP[status] || status
    },

    // ステータスラベルから値を取得
    getStatusValueFromLabel(label) {
      const entry = Object.entries(this.STATUS_MAP).find(([key, value]) => value === label)
      return entry ? entry[0] : label
    },

    // JSONから日付を取得するヘルパー
    getDateFromJson(item, category) {
      if (!item.dateJson || !item.dateJson[category]) return '-'
      const dateValue = item.dateJson[category].date
      if (!dateValue || dateValue === '1970-01-11') return '-'
      return formatDate(dateValue, '/')
    },

    // 36協定一覧取得API呼び出し
    async getAgreements() {
      try {
        const response = await apiService.get('api/article-36-agreements')

        this.agreements = response.agreements || []

        // JSONを整形
        this.agreements = this.agreements.map(item => this.parseJsonFields(item))

        return true
      } catch (error) {
        return false
      }
    },

    // 36協定詳細取得API呼び出し
    async getAgreementDetail(id) {
      if (!id) return null
      try {
        const response = await apiService.get(`/api/article-36-agreements/${id}`)
        if (response.agreement) {
          // JSON変換を行ってからディープコピーしてフォームデータに設定
          const parsedAgreement = this.parseJsonFields(response.agreement)
          this.formData = JSON.parse(JSON.stringify(parsedAgreement))
          return this.formData
        }
        return null
      } catch (error) {
        return null
      }
    },

    // 会社名リストを取得
    async getCompanyNames() {
      try {
        const response = await apiService.get('/api/companies/names')
        this.companyNames = response.companies || []
        return this.companyNames
      } catch (error) {
        this.companyNames = []
        return []
      }
    },

    // 会社IDで事業場リスト（管轄・監督署コード付き）を取得
    async getBranchesWithLaborCodes(companyId) {
      try {
        const response = await apiService.get(`/api/companies/${companyId}/branches-with-labor-codes`)
        this.branchNames = response.branches || []
        return this.branchNames
      } catch (error) {
        this.branchNames = []
        return []
      }
    },

    // 編集フォームデータの設定
    async setFormData(item = null) {
      if (item && item.id) {
        await this.getAgreementDetail(item.id)

        // APIから取得したデータでbranchIdがnullになっていれば、削除された事業場
        this.isDeletedBranch = item.branchId === null

        // 関連データを取得
        if (this.formData.companyId) {
          await this.getBranchesWithLaborCodes(this.formData.companyId)

          // 事業場が存在するかチェック
          if (this.formData.branchId !== null) {
            const branchExists = this.branchNames.some(branch => branch.id === this.formData.branchId)
            if (!branchExists) {
              // APIから取得したbranchIdが事業場リストに存在しない場合も削除された事業場と判断
              this.isDeletedBranch = true
            }
          }
        } else {
          // 会社IDがnullの場合、事業場も選択できない状態にする
          this.branchNames = []
        }

        // 日付JSONの整形
        if (this.formData.dateJson) {
          for (const key in this.formData.dateJson) {
            const dateInfo = this.formData.dateJson[key]
            if (dateInfo.date) {
              dateInfo.date = formatDate(dateInfo.date, '-')
            }
            // チェックボックスの初期化（1970-01-11の場合はチェックを外す）
            if (dateInfo.date === '1970-01-11') {
              dateInfo.isChecked = false
              dateInfo.date = ''
            } else if (dateInfo.date) {
              dateInfo.isChecked = true
            }
          }
        }

        // 終了予定日のフォーマット処理
        if (this.formData.scheduledEndDate) {
          this.formData.scheduledEndDate = formatDate(this.formData.scheduledEndDate, '-')
        }
      } else {
        // 新規作成の場合は初期値を使用
        this.formData = JSON.parse(JSON.stringify(this.formInit))
        this.branchNames = []
        this.isDeletedBranch = false
      }
    },

    // 会社変更時の処理
    async onCompanyChange() {
      if (!this.formData) return

      // 会社変更時、削除された事業場フラグをリセット
      this.isDeletedBranch = false

      // 事業場をリセット
      this.formData.branchId = null
      this.formData.laborBureauCode = ''
      this.formData.laborOfficeCode = ''

      // 事業場オプションを取得（管轄・監督署コード付き）
      await this.getBranchesWithLaborCodes(this.formData.companyId)
    },

    // 事業場変更時の処理
    async onBranchChange() {
      if (!this.formData || !this.formData.branchId) return

      // 選択された事業場の情報を取得して管轄・監督署を自動設定
      const selectedBranch = this.branchNames.find(branch => branch.id === this.formData.branchId)
      if (selectedBranch) {
        // 管轄・監督署コードを自動設定
        this.formData.laborBureauCode = selectedBranch.laborBureauCode || ''
        this.formData.laborOfficeCode = selectedBranch.laborOfficeCode || ''
      }
    },

    // チェックボックス変更時の処理
    updateCheckboxState(category) {
      if (!this.formData?.dateJson?.[category]) return

      const checkItem = this.formData.dateJson[category]

      if (checkItem.isChecked) {
        const userStore = useUserStore()

        // チェックされた場合：現在の日時とユーザー情報を設定
        checkItem.date = formatDate(new Date(), '-')
        checkItem.userId = userStore.currentUserId
        checkItem.userName = userStore.currentUserShortName
      } else {
        // チェックが外された場合：チェック情報をクリア
        checkItem.date = ''
        checkItem.userId = null
        checkItem.userName = ''
      }
    },

    // 日付変更時の処理（新規追加）
    updateDateInfo(category, date) {
      if (!this.formData?.dateJson?.[category]) return

      const dateItem = this.formData.dateJson[category]

      // 日付が空の場合はチェックを外す
      if (!date) {
        dateItem.date = ''
        dateItem.userId = null
        dateItem.userName = ''
        dateItem.isChecked = false
      } else {
        const userStore = useUserStore()
        dateItem.date = formatDate(date, '-')
        dateItem.userId = userStore.currentUserId
        dateItem.userName = userStore.currentUserShortName
        dateItem.isChecked = true
      }
    },

    // 36協定保存API呼び出し
    async saveAgreement() {
      try {
        // ディープコピーを作成
        const saveData = JSON.parse(JSON.stringify(this.formData))

        // JSONフィールドを文字列に変換
        saveData.dateJson = JSON.stringify(saveData.dateJson)
        saveData.colorInfo = JSON.stringify(saveData.colorInfo)

        const response = await apiService.post('/api/article-36-agreements', saveData)

        // 一覧を再取得
        await this.getAgreements()
        return response.message || '36協定データを登録しました。'
      } catch (error) {
        return null
      }
    },

    // 36協定削除API呼び出し
    async deleteAgreement() {
      if (!this.formData || !this.formData.id) return null

      try {
        const response = await apiService.delete(`/api/article-36-agreements/${this.formData.id}`)

        // 一覧を再取得
        await this.getAgreements()
        return response.message || '36協定データを削除しました。'
      } catch (error) {
        return null
      }
    },

    // コピー処理
    copyAgreement() {
      if (!this.formData) return false

      // IDをリセットして新規として扱う
      this.formData.id = null

      return true
    },

    // 共通JSON変換処理
    parseJsonFields(item) {
      if (!item) return item

      const parsedItem = { ...item }

      try {
        // dateJsonを文字列からオブジェクトに変換
        if (typeof parsedItem.dateJson === 'string') {
          parsedItem.dateJson = JSON.parse(parsedItem.dateJson)
        }
      } catch (error) {
        parsedItem.dateJson = JSON.parse(JSON.stringify(this.formInit.dateJson))
      }

      try {
        // colorInfoを文字列からオブジェクトに変換
        if (typeof parsedItem.colorInfo === 'string') {
          parsedItem.colorInfo = JSON.parse(parsedItem.colorInfo)
        }
      } catch (error) {
        parsedItem.colorInfo = JSON.parse(JSON.stringify(this.formInit.colorInfo))
      }

      return parsedItem
    },
  },

  getters: {
    // 事業場セレクトの状態を返す（"select"/"deleted"/null）
    getBranchSelectState:
      state =>
      (isEdit = false) => {
        const formData = state.formData

        // フォームデータがない場合は選択状態を返す
        if (!formData) return 'select'

        const branchId = formData.branchId
        const companyId = formData.companyId

        // 会社が選択されていない場合、事業場も選択できない
        if (!companyId) {
          return null // disabled状態
        }

        // 編集モードで、削除された事業場フラグがtrueの場合
        if (isEdit && state.isDeletedBranch) {
          return 'deleted'
        }

        // branchIdがnullで会社が選択されている場合は「選択」状態
        if (branchId === null && companyId) {
          return 'select'
        }

        return null // 正常に選択されている状態
      },

    // 会社選択の状態を返す
    getCompanySelectState:
      state =>
      (isEdit = false) => {
        const formData = state.formData
        if (!formData) return null

        const companyId = formData.companyId

        // 編集モードでcompanyIdがnull
        if (isEdit && companyId === null) {
          return 'deleted'
        }

        // 会社が選択されていない場合（新規登録時）
        if (companyId === null) {
          return 'select'
        }

        return null // 正常に選択されている状態
      },

    // ステータスオプションを取得
    statusOptions(state) {
      return Object.entries(state.STATUS_MAP).map(([value, label]) => ({
        value,
        label,
      }))
    },

    // 会社名リストからセレクトボックス用の選択肢を生成
    companyNameOptions(state) {
      return state.companyNames.map(company => ({
        label: company.name,
        value: company.id,
      }))
    },

    // 事業場名リストからセレクトボックス用の選択肢を生成
    branchNameOptions(state) {
      return state.branchNames.map(branch => ({
        label: branch.name,
        value: branch.id,
      }))
    },

    // チェック情報を取得するゲッター
    getCheckInfo: state => category => {
      if (!state.formData?.dateJson?.[category]?.date || !state.formData.dateJson[category].isChecked) return null
      const userStore = useUserStore()

      const userName = state.formData.dateJson[category].userName || '不明なユーザー'

      // ユーザーストアの省略表示メソッドを使用
      const truncatedName = userStore.getTruncatedUserName(userName, 48)

      return `${truncatedName}`
    },

    // ステータス別の件数
    statusCounts: state => {
      const counts = {
        total: state.agreements.length,
        in_progress: 0,
        waiting: 0,
        completed: 0,
        confirmation: 0,
      }

      state.agreements.forEach(agreement => {
        counts[agreement.status] = (counts[agreement.status] || 0) + 1
      })

      return counts
    },
  },
})
